import type { CambioEvento, ComoSeEntra, Evento, TipoEntrada } from '../../api/ops/eventos';
import { aCentimos, dinero } from '../../logica/dinero';
import { aCampo, deCampo } from './reglas';

/**
 * Lo editable de la ficha (Datos, Entradas y Puerta) como texto de los campos, y el `EventUpdate` con solo lo que cambia.
 * Publicado, las fechas no se cambian aquí (`changeEventDate`, pestaña Fecha).
 */
export interface FormEvento {
    nombre: string;
    descripcion: string;
    sede: string;
    inicio: string;
    fin: string;
    visibilidad: 'public' | 'unlisted';
    edad: string;
    cartel: string | null;
    modo: ComoSeEntra;
    nominativas: boolean;
    maximo: string;
    aforo: string;
    cupo: string;
    puertas: string;
}

export function formDe(e: Evento): FormEvento {
    return {
        nombre: e.name ?? '',
        descripcion: e.description ?? '',
        sede: e.location_id ?? '',
        inicio: aCampo(e.starts_at, e.timezone),
        fin: aCampo(e.ends_at, e.timezone),
        visibilidad: e.visibility ?? 'public',
        edad: e.age_notice_min_age == null ? '' : String(e.age_notice_min_age),
        cartel: e.image_ref ?? null,
        modo: e.sale_mode ?? 'tickets',
        nominativas: e.nominative === true,
        maximo: e.max_tickets_per_order === undefined ? '' : String(e.max_tickets_per_order),
        aforo: String(e.capacity ?? e.capacity_detail.capacity),
        cupo: String(e.box_office_quota ?? 0),
        puertas: aCampo(e.door_opens_at, e.timezone),
    };
}

export function erroresForm(f: FormEvento, e: Evento): Record<string, string> {
    const r: Record<string, string> = {};
    if (f.nombre.trim().length < 2) {
        r.name = 'Escribe el nombre del evento.';
    }
    if (e.status === 'draft') {
        const inicio = deCampo(f.inicio, e.timezone);
        const fin = deCampo(f.fin, e.timezone);
        if (inicio === null) {
            r.starts_at = 'Elige el día y la hora.';
        }
        if (fin === null) {
            r.ends_at = 'Elige cuándo termina.';
        } else if (inicio !== null && fin <= inicio) {
            r.ends_at = 'Termina después de empezar.';
        }
    }
    const maximo = Number(f.maximo);
    if (f.maximo.trim() !== '' && (!Number.isInteger(maximo) || maximo < 1 || maximo > 100)) {
        r.max_tickets_per_order = 'Entre 1 y 100.';
    }
    const aforo = Number(f.aforo);
    const cupo = Number(f.cupo === '' ? '0' : f.cupo);
    if (!Number.isInteger(aforo) || aforo < 1) {
        r.capacity = 'Escribe el aforo (1 o más).';
    }
    if (!Number.isInteger(cupo) || cupo < 0) {
        r.box_office_quota = 'Escribe un número (0 o más).';
    } else if (Number.isInteger(aforo) && cupo > aforo) {
        r.box_office_quota = 'No puede ser mayor que el aforo.';
    }
    if (f.puertas !== '' && deCampo(f.puertas, e.timezone) === null) {
        r.door_opens_at = 'Elige la hora.';
    }
    return r;
}

/** Solo los campos que cambian (vacío si nada). */
export function cambiosDe(e: Evento, f: FormEvento): CambioEvento {
    const antes = formDe(e);
    const c: CambioEvento = {};
    if (f.nombre.trim() !== antes.nombre) {
        c.name = f.nombre.trim();
    }
    if (f.descripcion !== antes.descripcion) {
        c.description = f.descripcion;
    }
    if (f.sede !== antes.sede && f.sede !== '') {
        c.location_id = f.sede;
    }
    if (e.status === 'draft') {
        if (f.inicio !== antes.inicio) {
            c.starts_at = deCampo(f.inicio, e.timezone) ?? '';
        }
        if (f.fin !== antes.fin) {
            c.ends_at = deCampo(f.fin, e.timezone) ?? '';
        }
    }
    if (f.visibilidad !== antes.visibilidad) {
        c.visibility = f.visibilidad;
    }
    if (f.edad !== antes.edad) {
        c.age_notice_min_age = f.edad === '' ? null : Number(f.edad);
    }
    if (f.cartel !== antes.cartel) {
        c.image_ref = f.cartel;
    }
    if (f.modo !== antes.modo) {
        c.sale_mode = f.modo;
    }
    if (f.nominativas !== antes.nominativas) {
        c.nominative = f.nominativas;
    }
    if (f.maximo !== antes.maximo && f.maximo.trim() !== '') {
        c.max_tickets_per_order = Number(f.maximo);
    }
    if (f.aforo !== antes.aforo) {
        c.capacity = Number(f.aforo);
    }
    if (f.cupo !== antes.cupo) {
        c.box_office_quota = Number(f.cupo === '' ? '0' : f.cupo);
    }
    if (f.puertas !== antes.puertas) {
        c.door_opens_at = f.puertas === '' ? null : deCampo(f.puertas, e.timezone);
    }
    return c;
}

/** Con entradas vendidas o pendientes, cómo se entra y la nominatividad ya no cambian (G3, G786). */
export function hayVendidas(e: Evento): boolean {
    return e.capacity_detail.tickets_confirmed > 0 || e.capacity_detail.tickets_held > 0;
}

export interface ValoresNuevo {
    nombre: string;
    sede: string;
    inicio: string;
    fin: string;
    modo: ComoSeEntra;
    aforo: string;
    cupo: string;
}

/** Errores de lo escrito antes de llamar a la API (la API vuelve a comprobarlo todo). */
export function erroresNuevo(v: ValoresNuevo, zona: string): Record<string, string> {
    const e: Record<string, string> = {};
    if (v.nombre.trim().length < 2) {
        e.name = 'Escribe el nombre del evento.';
    }
    if (v.sede === '') {
        e.location_id = 'Elige la sede.';
    }
    const inicio = deCampo(v.inicio, zona);
    const fin = deCampo(v.fin, zona);
    if (inicio === null) {
        e.starts_at = 'Elige el día y la hora.';
    }
    if (fin === null) {
        e.ends_at = 'Elige cuándo termina.';
    } else if (inicio !== null && fin <= inicio) {
        e.ends_at = 'Termina después de empezar.';
    }
    const aforo = Number(v.aforo);
    const cupo = v.cupo.trim() === '' ? 0 : Number(v.cupo);
    if (!Number.isInteger(aforo) || aforo < 1) {
        e.capacity = 'Escribe el aforo (1 o más).';
    }
    if (!Number.isInteger(cupo) || cupo < 0) {
        e.box_office_quota = 'Escribe un número (0 o más).';
    } else if (Number.isInteger(aforo) && cupo > aforo) {
        e.box_office_quota = 'No puede ser mayor que el aforo.';
    }
    return e;
}


export const NOTA_GASTOS = 'Los gastos de gestión (1 € + 1,5 % por entrada) los paga el comprador, aparte del precio. Tú recibes el precio entero, menos la comisión de Stripe.';

export function precioTipo(t: TipoEntrada): { precio: string; paga: string } {
    if (t.price === undefined) {
        return { precio: '', paga: '' };
    }
    if (t.price.amount === 0) {
        return { precio: 'Gratis', paga: '' };
    }
    return { precio: dinero(t.price), paga: t.price_with_fees === undefined ? '' : `paga ${dinero(t.price_with_fees)}` };
}

export interface FormValores {
    nombre: string;
    descripcion: string;
    precio: string;
    conCupo: boolean;
    cupo: string;
    desde: string;
    hasta: string;
    minimo: string;
    maximo: string;
    online: boolean;
    taquilla: boolean;
    color: string | null;
}

export function erroresTipo(v: FormValores, vendidas: number): Record<string, string> {
    const r: Record<string, string> = {};
    if (v.nombre.trim().length < 2) {
        r.name = 'Escribe el nombre del tipo.';
    }
    if (v.precio.trim() === '') {
        r.price = 'Escribe el precio (0 = gratis).';
    } else if (aCentimos(v.precio) === null) {
        r.price = 'Escribe un importe, por ejemplo 10 o 12,50.';
    }
    const cupo = Number(v.cupo);
    if (v.conCupo && (!Number.isInteger(cupo) || cupo < 1)) {
        r.quota = 'Escribe el cupo (1 o más).';
    } else if (v.conCupo && cupo < vendidas) {
        r.quota = `No puede bajar de lo vendido (${String(vendidas)}).`;
    }
    const minimo = Number(v.minimo);
    const maximo = Number(v.maximo);
    if (!Number.isInteger(minimo) || minimo < 1) {
        r.min_per_order = 'Al menos 1.';
    }
    if (!Number.isInteger(maximo) || maximo < 1 || maximo > 100) {
        r.max_per_order = 'Entre 1 y 100.';
    } else if (Number.isInteger(minimo) && maximo < minimo) {
        r.max_per_order = 'No menos que el mínimo.';
    }
    if (!v.online && !v.taquilla) {
        r.sold_online = 'Elige dónde se vende.';
    }
    return r;
}

